import { useEffect, useState } from "react";
import { httpsCallable } from "firebase/functions";
import { Link } from "react-router-dom";
import { collection, getDocs } from "firebase/firestore";
import { db, functions } from "../../services/firebase";

const createUserAccount = httpsCallable(functions, "createUserAccount");

function getCreateAccountError(error) {
  const code = String(error?.code || "");

  if (code.includes("already-exists")) return "Email is already in use.";
  if (code.includes("invalid-argument")) return error.message || "Check the account details.";
  if (code.includes("permission-denied")) return "You do not have permission to create users.";
  if (code.includes("unauthenticated")) return "Please log in again.";

  return error?.message || "Account creation failed. Please try again.";
}

export default function SignUp({ onSignUp }) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [role, setRole] = useState("driver");
  const [companies, setCompanies] = useState([]);
  const [companyIds, setCompanyIds] = useState([]);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const snap = await getDocs(collection(db, "companies"));
        const list = snap.docs
          .map((d) => ({ id: d.id, name: d.data()?.name || d.id }))
          .sort((a, b) => String(a.name || "").localeCompare(b.name || ""));
        setCompanies(list);
      } catch (err) {
        console.warn("Failed to load companies for account creation:", err);
      }
    })();
  }, []);

  const handleSignUp = async (e) => {
    e.preventDefault();
    setError("");
    setSuccess("");

    const cleanEmail = email.trim();
    if (password.length < 6) {
      setError("Password should be at least 6 characters.");
      return;
    }

    setSaving(true);

    try {
      const result = await createUserAccount({
        email: cleanEmail,
        password,
        role,
        companyIds: role === "driver" ? companyIds : [],
      });

      const createdEmail = result.data?.email || cleanEmail;
      setSuccess(`Created ${createdEmail} as ${role}.`);
      setEmail("");
      setPassword("");
      setRole("driver");
      setCompanyIds([]);
      onSignUp?.(result.data);
    } catch (err) {
      setError(getCreateAccountError(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 px-4 py-8">
      <div className="mx-auto w-full max-w-2xl space-y-4">
        <div className="rounded-2xl border border-gray-200 bg-white p-6 shadow-lg sm:p-8">
          <div className="mb-6 flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
            <div>
              <h2 className="text-2xl font-semibold text-gray-900">
                Create account
              </h2>
              <p className="mt-1 text-sm text-gray-500">
                Create driver or admin accounts without switching out of your login.
              </p>
            </div>

            <Link
              to="/admin/reset-password"
              className="inline-flex items-center justify-center rounded-lg border border-gray-300 px-3 py-2 text-sm font-medium text-gray-700 no-underline transition hover:bg-gray-50"
            >
              Reset password
            </Link>
          </div>

          {error && (
            <p className="mb-4 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
              {error}
            </p>
          )}

          {success && (
            <p className="mb-4 rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-2 text-sm text-emerald-700">
              {success}
            </p>
          )}

          <form onSubmit={handleSignUp} className="space-y-4">
            <label className="block">
              <span className="block text-sm font-medium text-gray-700">
                Email
              </span>
              <input
                type="email"
                autoComplete="off"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="mt-1 w-full rounded-lg border border-gray-300 px-3 py-2 text-sm shadow-sm focus:border-emerald-400 focus:ring-emerald-400"
              />
            </label>

            <label className="block">
              <span className="block text-sm font-medium text-gray-700">
                Temporary password
              </span>
              <input
                type="password"
                autoComplete="new-password"
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="mt-1 w-full rounded-lg border border-gray-300 px-3 py-2 text-sm shadow-sm focus:border-emerald-400 focus:ring-emerald-400"
              />
            </label>

            <label className="block">
              <span className="block text-sm font-medium text-gray-700">
                Role
              </span>
              <select
                value={role}
                onChange={(e) => setRole(e.target.value)}
                className="mt-1 w-full rounded-lg border border-gray-300 px-3 py-2 text-sm shadow-sm focus:border-emerald-400 focus:ring-emerald-400"
              >
                <option value="driver">Driver</option>
                <option value="admin">Admin</option>
              </select>
            </label>

            {role === "driver" && (
              <div className="rounded-xl border border-gray-200 bg-gray-50 p-4">
                <div className="text-sm font-medium text-gray-700">
                  Driver companies
                </div>
                <div className="mt-2 grid gap-2 sm:grid-cols-2">
                  {companies.length ? (
                    companies.map((company) => {
                      const checked = companyIds.includes(company.id);
                      return (
                        <label
                          key={company.id}
                          className={`flex cursor-pointer items-center gap-2 rounded-lg border px-3 py-2 text-sm ${
                            checked
                              ? "border-emerald-300 bg-emerald-50 text-emerald-900"
                              : "border-gray-200 bg-white text-gray-700"
                          }`}
                        >
                          <input
                            type="checkbox"
                            checked={checked}
                            onChange={() =>
                              setCompanyIds((current) =>
                                checked
                                  ? current.filter((id) => id !== company.id)
                                  : [...current, company.id],
                              )
                            }
                            className="h-4 w-4 rounded border-gray-300 text-emerald-600 focus:ring-emerald-500"
                          />
                          <span className="min-w-0 truncate">{company.name}</span>
                        </label>
                      );
                    })
                  ) : (
                    <div className="text-sm text-gray-500">
                      No companies found. You can assign companies later in the data hub.
                    </div>
                  )}
                </div>
              </div>
            )}

            <button
              type="submit"
              disabled={saving}
              className="w-full rounded-lg bg-emerald-600 py-2.5 text-sm font-medium text-white transition hover:bg-emerald-700 disabled:bg-gray-400"
            >
              {saving ? "Creating..." : "Create account"}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
}
